import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { LABEL } from '@/components/ui/formFields'
import { COUNTRIES } from '@/data/countries'

/** Diacritic- and apostrophe-insensitive: "Åland" -> "aland", "Côte d'Ivoire". */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[’']/g, "'")
    .toLowerCase()
    .trim()
}

interface FoldedOption extends ComboboxOption {
  folded: string
}

const OPTIONS: FoldedOption[] = COUNTRIES.map((country) => ({
  value: country.code,
  label: country.name,
  folded: fold(country.name),
}))

function filterCountries(
  query: string,
  options: readonly ComboboxOption[],
): ComboboxOption[] {
  const q = fold(query)
  if (!q) {
    return [...options]
  }

  const byCode =
    q.length === 2
      ? OPTIONS.find((o) => o.value.toLowerCase() === q)
      : undefined

  const prefix: ComboboxOption[] = []
  const substring: ComboboxOption[] = []
  for (const option of OPTIONS) {
    if (option === byCode) {
      continue
    }
    if (option.folded.startsWith(q)) {
      prefix.push(option)
    } else if (option.folded.includes(q)) {
      substring.push(option)
    }
  }

  return byCode ? [byCode, ...prefix, ...substring] : [...prefix, ...substring]
}

interface CountryFieldProps {
  id: string
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  /** Label text — defaults to "Country". */
  label?: string
  /** Hide the label visually (Browse filter row). */
  labelHidden?: boolean
  hint?: string
}

export function CountryField({
  id,
  value,
  onChange,
  disabled,
  label = 'Country',
  labelHidden,
  hint,
}: CountryFieldProps) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className={labelHidden ? 'sr-only' : LABEL}>
        {label}
      </label>
      <Combobox
        id={id}
        value={value}
        onChange={onChange}
        options={OPTIONS}
        filter={filterCountries}
        disabled={disabled}
        placeholder="Anywhere"
        listLabel="Countries"
        clearLabel="Clear country"
        emptyMessage="No match. Pick from the list, or leave it as Anywhere."
      />
      {hint && <p className="font-body text-label-sm text-fg-muted">{hint}</p>}
    </div>
  )
}
